/**
 * Traffic › page detail (`/traffic/page?path=%2Fpricing&range=30`) —
 * the page-focused traffic view reached by clicking a row on the
 * Traffic Pages card.
 *
 * Keyed by the raw `page_path` string in a search param (page paths
 * contain slashes, and RUM paths aren't guaranteed to have a pg-*
 * entity). `range` also lives in the URL — unlike `/traffic`'s local
 * state — so back/forward and shared links land on the same window.
 *
 * Data: KPIs + trend read the pages MV (full retention); the
 * breakdown cards read raw events (plan-retention limited — the
 * caption under the grid names the truncation when it applies).
 */
import {
  DEFAULT_PAGE_RANGE,
  type PageRange,
  PageRangePill,
} from '@/components/primitives/PageRangePill';
import {
  PageSessionsCard,
  PageTrafficKpiRow,
  RetentionCaption,
} from '@/components/rum/PageTrafficView';
import { RumBreakdownGrid, RumTrendChart } from '@/components/rum/RumPlaceholders';
import { SiteIsland } from '@/components/shell/SiteIsland';
import { useActiveSiteUuid } from '@/lib/active-site';
import { usePagePixelTraffic } from '@/lib/queries/pixel';
import { createFileRoute, redirect } from '@tanstack/react-router';
import type { ReactElement } from 'react';

const PAGE_RANGES: readonly PageRange[] = [7, 30, 90, 180, 365];

interface PageTrafficSearch {
  path: string;
  range?: PageRange;
}

export const Route = createFileRoute('/_app/traffic/page')({
  validateSearch: (search: Record<string, unknown>): PageTrafficSearch => ({
    path: typeof search.path === 'string' ? search.path : '',
    ...(PAGE_RANGES.includes(search.range as PageRange)
      ? { range: search.range as PageRange }
      : {}),
  }),
  beforeLoad: ({ search }) => {
    // No page to focus on — land back on the Traffic overview rather
    // than rendering an all-empty detail shell.
    if (!search.path) throw redirect({ to: '/traffic' });
  },
  component: PageTrafficPage,
});

const compactFmt = new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 });

function PageTrafficPage(): ReactElement {
  const siteUuid = useActiveSiteUuid();
  const { path, range: rangeParam } = Route.useSearch();
  const navigate = Route.useNavigate();
  const range = rangeParam ?? DEFAULT_PAGE_RANGE;

  const traffic = usePagePixelTraffic(siteUuid, { path, days: range });

  return (
    <SiteIsland section={path} parent={{ label: 'Traffic', href: '/traffic' }}>
      {siteUuid && (
        <div className="space-y-6">
          <div className="flex items-center gap-2">
            <div className="min-w-0 flex-1">
              <PageTrafficSummary
                data={traffic.data ?? undefined}
                loading={traffic.isLoading}
                path={path}
              />
            </div>
            <PageRangePill
              value={range}
              onChange={(next) =>
                navigate({ search: (prev) => ({ ...prev, range: next }), replace: true })
              }
            />
          </div>
          <PageTrafficKpiRow data={traffic.data} />
          <RumTrendChart scope="page" siteUuid={siteUuid} pagePath={path} range={range} />
          <RumBreakdownGrid
            cards={['sources', 'geo', 'devices']}
            siteUuid={siteUuid}
            days={range}
            pagePath={path}
          />
          <PageSessionsCard siteUuid={siteUuid} path={path} days={range} />
          <RetentionCaption range={range} />
        </div>
      )}
    </SiteIsland>
  );
}

/** One-line recap mirroring the Traffic overview's summary — the page
 *  path plus its headline counts for the window. */
function PageTrafficSummary({
  data,
  loading,
  path,
}: {
  data: { pageviews: number; visitors: number } | undefined;
  loading: boolean;
  path: string;
}): ReactElement {
  const views = loading ? '—' : compactFmt.format(data?.pageviews ?? 0);
  const visitors = loading ? '—' : compactFmt.format(data?.visitors ?? 0);
  return (
    <p className="truncate text-sm text-[var(--color-mute)]">
      <span className="font-medium text-[var(--color-ink)]" title={path}>
        {path}
      </span>
      <span className="mx-1.5 text-[var(--color-line)]">·</span>
      <span className="font-semibold tabular-nums text-[var(--color-ink)]">{views}</span> views
      <span className="mx-1.5 text-[var(--color-line)]">·</span>
      <span className="font-semibold tabular-nums text-[var(--color-ink)]">{visitors}</span>{' '}
      visitors
    </p>
  );
}
